// src/services/AuthService.ts
const API_URL = 'https://ltq6ct6fde.execute-api.us-east-2.amazonaws.com/prod/auth';

// Helper function to get API key
const getApiKey = () => {
  return process.env.REACT_APP_API_KEY_AUTH || '';
};

// Authenticate an admin
export const authenticateAdmin = async (registryId: string, password: string): Promise<boolean> => {
  try {
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': getApiKey()
      },
      body: JSON.stringify({
        operation: 'authenticate',
        registryId,
        password
      })
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Auth API error:', errorText);
      return false;
    }
    
    const data = await response.json();
    return data.isAuthenticated;
  } catch (error) {
    console.error('Authentication error:', error);
    return false;
  }
};

// Get a hashed password
export const getHashedPassword = async (password: string): Promise<string> => {
  try {
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': getApiKey()
      },
      body: JSON.stringify({
        operation: 'hashPassword',
        password
      })
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }
    
    const data = await response.json();
    return data.hashedPassword;
  } catch (error) {
    console.error('Password hashing error:', error);
    throw error;
  }
};

// Request password reset
export const requestPasswordReset = async (registryId: string): Promise<boolean> => {
  try {
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': getApiKey()
      },
      body: JSON.stringify({
        operation: 'requestPasswordReset',
        registryId
      })
    });
    
    if (!response.ok) {
      return false;
    }
    
    const data = await response.json();
    return data.success;
  } catch (error) {
    console.error('Password reset request error:', error);
    return false;
  }
};

export const requestPasswordResetByEmail = async (email: string): Promise<boolean> => {
  try {
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': getApiKey()
      },
      body: JSON.stringify({
        operation: 'requestPasswordResetByEmail',
        email: email
      })
    });

    const data = await response.json();
    return data.success;
  } catch (error) {
    console.error('Error requesting password reset by email:', error);
    return false;
  }
};

// Validate reset token
export const validateResetToken = async (token: string): Promise<{valid: boolean, registryId?: string}> => {
  try {
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': getApiKey()
      },
      body: JSON.stringify({
        operation: 'validateResetToken',
        token
      })
    });
    
    if (!response.ok) {
      return { valid: false };
    }
    
    const data = await response.json();
    if (!data.success) {
      return { valid: false };
    }
    
    return { valid: data.valid, registryId: data.registryId };
  } catch (error) {
    console.error('Token validation error:', error);
    return { valid: false };
  }
};

// Reset password using token
export const resetPassword = async (token: string, newPassword: string): Promise<boolean> => {
  try {
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': getApiKey()
      },
      body: JSON.stringify({
        operation: 'resetPassword',
        token,
        newPassword
      })
    });
    
    if (!response.ok) {
      return false;
    }
    
    const data = await response.json();
    return data.success;
  } catch (error) {
    console.error('Password reset error:', error);
    return false;
  }
};

export const findMommaFuel = async (email: string, password: string): Promise<string | null> => {
  try {
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': getApiKey()
      },
      body: JSON.stringify({
        operation: 'findMommaFuel',
        email,
        password
      })
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    return data.success ? data.registryId : null;
  } catch (error) {
    console.error('Find MommaFuel error:', error);
    return null;
  }
};